/**
 * Kiosk Substitution Display Page
 * 
 * Entry point for the Vertretungsplan kiosk display.
 * Validates kiosk session and renders the KioskVertretungsplanLayout component.
 * Automatically redirects to /kiosk/setup if session is invalid.
 * 
 * @module kiosk/display-substitution
 */

import { useEffect, useState } from 'react';
import { DebugOverlay } from '../../debug';
import KioskVertretungsplanLayout from './components/KioskVertretungsplanLayout';
import {
    getKioskToken,
    decodeJwtPayload,
    clearKioskSession
} from './utils/kioskUtils';

// ============================================================================
// Types
// ============================================================================

interface KioskInfo {
    kioskId: string | null;
    schoolId: string | null;
    allowedViews: string[];
    kioskName: string;
}

// ============================================================================
// Component
// ============================================================================

export default function KioskSubstitutionDisplayPage() {
    const [loading, setLoading] = useState(true);
    const [kioskInfo, setKioskInfo] = useState<KioskInfo | null>(null);

    useEffect(() => {
        let cancelled = false;

        const checkKioskSession = (): void => {
            try {
                if (cancelled) return;

                const token = getKioskToken();
                if (!token) {
                    // No token - redirect to setup immediately
                    clearKioskSession();
                    return;
                }

                const payload = decodeJwtPayload(token);
                if (!payload || payload.role !== 'kiosk') {
                    // Invalid session - redirect to setup immediately
                    clearKioskSession();
                    return;
                }

                setKioskInfo({
                    kioskId: payload.kiosk_id || payload.sub || null,
                    schoolId: payload.school_id || null,
                    allowedViews: Array.isArray(payload.allowed_views) ? payload.allowed_views : [],
                    kioskName: payload.kiosk_name || 'Kiosk Display'
                });
                setLoading(false);
            } catch {
                if (cancelled) return;
                // Error checking session - redirect to setup
                clearKioskSession();
            }
        };

        checkKioskSession();

        return () => {
            cancelled = true;
        };
    }, []);

    // Show loading while checking session (brief flash before redirect if invalid)
    if (loading) {
        return (
            <DebugOverlay name="KioskSubstitutionDisplayPage">
                <div className="min-h-screen flex items-center justify-center bg-slate-900 text-slate-50">
                    <p className="text-sm">Kiosk-Anzeige wird geladen…</p>
                </div>
            </DebugOverlay>
        );
    }

    // Render substitution layout
    return (
        <DebugOverlay name="KioskSubstitutionDisplayPage">
            <KioskVertretungsplanLayout kioskInfo={kioskInfo} />
        </DebugOverlay>
    );
}
